<template>
  <div class="Address">
    <EVAlert
      :show="showAlert"
      fade
      :count="3"
      message="Beide velden zijn aangepast!"
      variant="success"
      position="bottom"
      style="z-index:3"
      @dismissed="showAlert = false"
    />
    <FieldSet
      :label="label"
      :values="values"
      :fields="fields"
      :validation="!disabled ? validation : null"
      :debounce="1000"
      :layout="coordlayout"
      :disabled="disabled"
      :path="path"
      @blur="handleBlur"
      @input="handleInput"
      @update="handleUpdate"
    />
    <AdditionalCoordinates
      v-if="getUseAdditionalCoordinates && !isAlternative && !isAssetProcess"
      :values="values"
      :record-values="recordValues"
      :disabled="locked || !canAddCoordinates"
      @setAdditionalCoordintaes="handleAdditionalCoordinates"
    />
    <div v-if="!coordinatesOnly">
      <strong>{{ $t('Address.publicationAddress') }}</strong>
      <FieldSet
        class="mt-2"
        :label="label"
        :values="values"
        :fields="fields"
        :validation="!disabled ? validation : null"
        :layout="layout"
        :disabled="disabled"
        :path="path"
        @blur="handleBlur"
        @input="handleInput"
      />
    </div>
  </div>
</template>

<script>
import FieldSet from '@/components/form/FieldSet';
import EVAlert from '@/components/EVAlert';
import AdditionalCoordinates from '@/components/definitions/AdditionalCoordinates';
import { mapGetters } from 'vuex';

import { required, helpers } from 'vuelidate/lib/validators';
import { isNumeric, unicodeString, isLatitude, isLongitude, isValidPostalcode } from '@/services/validation';

export default {
  name: 'Address',
  components: {
    FieldSet,
    EVAlert,
    AdditionalCoordinates
  },
  props: {
    label: {
      type: String,
      default: ''
    },
    values: {
      type: Object,
      default: function () {
        return {};
      }
    },
    path: {
      type: String,
      default: 'Location'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    locked: {
      type: Boolean,
      default: false
    },
    recordValues: {
      type: Object,
      default: () => {}
    },
    isAlternative: {
      type: Boolean,
      default: false
    },
    coordinatesOnly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      layout: [
        {
          columns: '2.1fr 1fr 1fr',
          fields: ['StreetName', 'HouseNumber', 'HouseNumberSuffix']
        },
        {
          columns: '1fr 1fr',
          fields: ['PostalCode', 'CityName']
        }
      ],
      coordlayout: [
        {
          columns: '1fr 1fr',
          fields: ['Latitude', 'Longitude']
        }
      ],
      validation: {
        StreetName: {
          required,
          unicodeString
        },
        HouseNumber: {
          required,
          isNumeric
        },
        HouseNumberSuffix: {
          unicodeString: value => !helpers.req(value) || unicodeString(value)
        },
        PostalCode: {
          required,
          isValidPostalcode: value => {
            // ONS/GSS (British municipalityCode) is based on a fixed nine-character, alpha-numeric structure //
            const isBritish = this.values?.Municipality?.length === 9
            return isValidPostalcode(value, false, isBritish)
          }
        },
        CityName: {
          required,
          unicodeString
        },
        Latitude: {
          required,
          isLatitude
        },
        Longitude: {
          required,
          isLongitude
        }
      },
      internalData: {},
      showAlert: false
    };
  },
  computed: {
    ...mapGetters('realisation', ['getSavedRecordValues', 'getActiveRecordValues']),
    ...mapGetters('realisations', ['getSelectedRecord']),
    ...mapGetters('processes', ['getSelectedProcess']),
    ...mapGetters('tenant', ['getUseAdditionalCoordinates']),
    canAddCoordinates() {
      return !!this.values?.Location?.StreetName;
    },
    fields () {
      return {
        StreetName: {
          type: 'string',
          title: this.$t('processes.fields.streetname')
        },
        HouseNumber: {
          type: 'number',
          title: this.$t('processes.fields.housenumber')
        },
        HouseNumberSuffix: {
          type: 'string',
          title: this.$t('processes.fields.housenumbersuffix')
        },
        PostalCode: {
          type: 'string',
          title: this.$t('processes.fields.postalcode')
        },
        CityName: {
          type: 'string',
          title: this.$t('processes.fields.city')
        },
        Latitude: {
          type: 'float',
          title: this.$t('processes.fields.latitude')
        },
        Longitude: {
          type: 'float',
          title: this.$t('processes.fields.longitude')
        }
      }
    },
    isAssetProcess () {
      return this.getSelectedProcess?.processType?.value !== 'realisation'
    }
  },
  watch: {
    getActiveRecordValues: {
      deep: true,
      immediate: true,
      handler(values) {
        if (values && !this.isAlternative) {
          this.internalData = values.Location;
        }
      }
    },
    values(old, values) {
      Object.keys(old.Location).forEach(key => {
        if (old.Location[key] !== values?.Location[key]) {
          delete this.internalData[`Location.${key}`];
        }
      });
    },
    locked: {
      handler(locked) {
        if (!this.isAlternative && locked) {
          this.internalData = this.getSavedRecordValues?.Location;
        }
      }
    }
  },
  created() {
    if (this.isAlternative) {
      this.internalData = this.values?.Location;
    }
  },
  methods: {
    handleInput({ name, value }) {
      const prop = name.split('.')[1];

      if (this.isAlternative) {
        this.internalData[prop] = value;
        this.handleUpdate({ name, value });
      } else {
        this.getActiveRecordValues[this.path][prop] = this.internalData[prop] = value;

        this.handleUpdate({ name, value });

        this.$store.dispatch('realisation/setActiveRecordValues', { values: this.getActiveRecordValues });
        if (this.getSelectedRecord) {
          this.getSelectedRecord.CurrentLocation = this.getActiveRecordValues
          this.$store.dispatch('realisations/setSelectedRecord', { record: this.getSelectedRecord })
        }
        //this.emitAddressUpdate();
      }
    },
    handleBlur({ name }) {
      if (!this.isAlternative) return;

      if (
        [
          'Location.StreetName',
          'Location.HouseNumber',
          'Location.HouseNumberSuffix',
          'Location.PostalCode',
          'Location.CityName'
        ].includes(name)
      ) {
        this.emitAddressUpdate();
      }
    },
    handleUpdate({ name, value }) {
      const prop = name.split('.')[1] || name;

      if (!['Latitude', 'Longitude'].includes(prop)) {
        return;
      }

      const coordinates = value
        .split(',')
        .map(entry => parseFloat(entry.trim()))
        .filter(Boolean);

      if (coordinates.length > 1) {
        const [latitude, longitude] = coordinates;

        this.internalData.Longitude = longitude;
        this.internalData.Latitude = latitude;
        this.showAlert = true;
      } else {
        this.internalData[prop] = parseFloat(value.trim());
      }

      this.emitCoordinateUpdate();
    },
    /**
     * Communicate the current coordinates (changed or not) after user interaction with the coordinate fields
     */
    emitCoordinateUpdate() {
      const lng = parseFloat(this.internalData.Longitude);
      const lat = parseFloat(this.internalData.Latitude);

      if (lng && lat) {
        this.$emit('coordinates', { lng, lat, manualInput: true });
      }
    },

    handleAdditionalCoordinates({ coordinates }) {
      if (this.getActiveRecordValues?.Location) {
        this.getActiveRecordValues.Location.AdditionalCoordinates = coordinates;
        this.$store.dispatch('realisation/setActiveRecordValues', { values: this.getActiveRecordValues });
      }
    },
    /**
     * Communicate the current coordinates (changed or not) after user interaction with the address fields
     */
    emitAddressUpdate() {
      const suffix = Object.prototype.hasOwnProperty.call(this.internalData, 'Location.HouseNumberSuffix')
        ? this.internalData['Location.HouseNumberSuffix']
        : this.values?.Location?.['HouseNumberSuffix'] || '';

      if (this.values?.Location) {
        this.$emit('address', {
          address: {
            StreetName: this.internalData['Location.StreetName'] || this.values.Location['StreetName'],
            HouseNumber: this.internalData['Location.HouseNumber'] || this.values.Location['HouseNumber'],
            HouseNumberSuffix: suffix,
            PostalCode: this.internalData['Location.PostalCode'] || this.values.Location['PostalCode'],
            CityName: this.internalData['Location.CityName'] || this.values.Location['CityName']
          }
        });
      }
    }
  }
};
</script>
<style lang="scss">
.Address {
  position: relative;
  .Row {
    column-gap: 1em;
  }
}
</style>
